import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { acceptanceTone, holdClock } from './types';

/** Commercial acceptance clock, independent of the soft inventory hold.
 *  Missing/invalid server deadlines are explicitly unknown, never expired. */
export function AcceptanceClock({ expiresAt, now }: { expiresAt: string | undefined; now: number }) {
  const { t, i18n } = useTranslation();
  const clock = holdClock(expiresAt, now);
  if (!clock.hasClock || !expiresAt) {
    return (
      <Badge variant="muted" size="sm" data-testid="acceptance-clock" data-clock="unknown">
        {t('holds.acceptance.unknown')}
      </Badge>
    );
  }
  const tone = acceptanceTone(clock);
  const until = new Date(expiresAt).toLocaleString(i18n.resolvedLanguage ?? i18n.language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  return (
    <Badge
      variant={tone}
      size="sm"
      data-testid="acceptance-clock"
      data-tone={tone}
      data-clock={clock.lapsed ? 'lapsed' : 'counting'}
      title={
        clock.lapsed
          ? t('holds.acceptance.lapsed', 'Acceptance window closed')
          : `${t('holds.clock.until', 'until')} ${until}`
      }
    >
      <Clock className="h-3 w-3" aria-hidden />
      {clock.lapsed ? (
        t('holds.acceptance.lapsed', 'Acceptance window closed')
      ) : (
        <span className="tabular-nums">
          {t('holds.acceptance.label', 'Accept within')} {clock.remaining}
        </span>
      )}
      <span className="font-normal opacity-70">
        · {t('holds.clock.until', 'until')} <time dateTime={expiresAt}>{until}</time>
      </span>
    </Badge>
  );
}

